import FirestoreBaseService from './firestoreBase';
import { USER_ROLES } from '../context/AuthContext';
import { writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

class UserService extends FirestoreBaseService {
  constructor() {
    super('users');
    this.participantEmailsService = new FirestoreBaseService('participant_emails');
    this.studentsService = new FirestoreBaseService('students');
    this.mentorsService = new FirestoreBaseService('mentors');
  }

  /**
   * ユーザーを作成（関連データも含めて）
   * @param {string} uid - ユーザーUID
   * @param {string} email - メールアドレス
   * @param {string} role - ユーザーロール
   * @param {Object} additionalData - 追加データ（学生またはOG情報）
   */
  async createUserWithRelatedData(uid, email, role, additionalData = {}) {
    try {
      const batch = writeBatch(db);

      // メインのユーザードキュメント
      const userRef = doc(db, 'users', uid);
      batch.set(userRef, {
        email,
        role,
        emailVerified: true,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // 参加者メールドキュメント
      const participantEmailRef = doc(db, 'participant_emails', uid);
      batch.set(participantEmailRef, {
        email,
        role,
        createdAt: new Date()
      });

      // ロール別の追加データ
      if (role === USER_ROLES.STUDENT && additionalData.studentData) {
        console.log('Creating student data for uid:', uid);
        console.log('Student data:', additionalData.studentData);
        const studentRef = doc(db, 'students', uid);
        batch.set(studentRef, {
          ...additionalData.studentData,
          email: email,  // Firestore rulesで必要
          createdAt: new Date()
        });
      } else if (role === USER_ROLES.MENTOR && additionalData.mentorData) {
        const mentorRef = doc(db, 'mentors', uid);
        batch.set(mentorRef, {
          ...additionalData.mentorData,
          urlParam: uid,
          createdAt: new Date()
        });
      }

      await batch.commit();
      console.log('User with related data created successfully for uid:', uid);
      return { success: true, uid };
    } catch (error) {
      console.error('Error creating user with related data:', error);
      throw this.handleError(error);
    }
  }

  /**
   * ユーザーの完全削除（関連データも含めて）
   * @param {string} uid - ユーザーUID
   */
  async deleteUserWithRelatedData(uid) {
    try {
      console.log('Starting user deletion process for uid:', uid);
      const batch = writeBatch(db);

      // メインのユーザードキュメント
      const userRef = doc(db, 'users', uid);
      batch.delete(userRef);
      console.log('Added users document deletion to batch');

      // 参加者メールドキュメント
      const participantEmailRef = doc(db, 'participant_emails', uid);
      batch.delete(participantEmailRef);
      console.log('Added participant_emails document deletion to batch');

      // 学生データ（存在する場合）
      const studentRef = doc(db, 'students', uid);
      batch.delete(studentRef);
      console.log('Added students document deletion to batch');

      // OGデータ（存在する場合）
      const mentorRef = doc(db, 'mentors', uid);
      batch.delete(mentorRef);
      console.log('Added mentors document deletion to batch');

      console.log('Committing batch deletion...');
      await batch.commit();
      console.log('User deletion completed successfully for uid:', uid);
      return { success: true, uid };
    } catch (error) {
      console.error('Error deleting user with related data:', error);
      console.error('Error details:', error.message, error.code);
      throw this.handleError(error);
    }
  }

  /**
   * ユーザーロールを取得
   * @param {string} uid - ユーザーUID
   */
  async getUserRole(uid) {
    try {
      // まずusersコレクションから取得を試行
      const userData = await this.get(uid);
      if (userData && userData.role) {
        return userData.role;
      }

      // usersコレクションにない場合、mentorsコレクションを確認
      const mentorData = await this.mentorsService.get(uid);
      if (mentorData) {
        // OGデータが存在する場合、usersドキュメントを作成
        await this.createOrUpdate(uid, {
          email: mentorData.連絡先 || '',
          role: USER_ROLES.MENTOR,
          emailVerified: true
        });

        // participant_emailsにも追加
        if (mentorData.連絡先) {
          await this.participantEmailsService.createOrUpdate(uid, {
            email: mentorData.連絡先,
            role: USER_ROLES.MENTOR
          });
        }

        return USER_ROLES.MENTOR;
      }

      // studentsコレクションも確認
      const studentData = await this.studentsService.get(uid);
      if (studentData) {
        // 学生データが存在する場合、usersドキュメントを作成
        await this.createOrUpdate(uid, {
          email: studentData.連絡先 || '',
          role: USER_ROLES.STUDENT,
          emailVerified: true
        });

        // participant_emailsにも追加
        if (studentData.連絡先) {
          await this.participantEmailsService.createOrUpdate(uid, {
            email: studentData.連絡先,
            role: USER_ROLES.STUDENT
          });
        }

        return USER_ROLES.STUDENT;
      }

      // どちらにもない場合はデフォルトロール
      return USER_ROLES.STUDENT;
    } catch (error) {
      console.error('Error getting user role:', error);
      return USER_ROLES.STUDENT;
    }
  }

  /**
   * ユーザーの存在確認（包括的チェック）
   * @param {string} email - メールアドレス
   * @param {boolean} skipInvitationCheck - 招待チェックをスキップするか
   */
  async checkUserExists(email) {
    try {
      // participant_emailsコレクションでの確認
      const participantEmails = await this.participantEmailsService.getMany([
        { field: 'email', operator: '==', value: email }
      ]);

      if (participantEmails.length > 0) {
        return true;
      }


      // 保留中の登録での確認
      const pendingRegistrationsService = new FirestoreBaseService('pending_registrations');
      const pendingId = email.replace(/[@.]/g, '_');
      const pendingRegistration = await pendingRegistrationsService.get(pendingId);
      
      if (pendingRegistration) {
        // 24時間以内の保留登録は有効
        const now = new Date();
        const pendingTime = pendingRegistration.timestamp?.toDate();
        const hoursDiff = pendingTime ? (now - pendingTime) / (1000 * 60 * 60) : 0;
        
        if (hoursDiff < 24) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking user existence:', error);
      // エラー時は安全側に倒してfalseを返す
      return false;
    }
  }

  /**
   * ユーザー情報の更新
   * @param {string} uid - ユーザーUID
   * @param {Object} userData - 更新するユーザーデータ
   */
  async updateUser(uid, userData) {
    try {
      return await this.update(uid, userData);
    } catch (error) {
      console.error('Error updating user:', error);
      throw this.handleError(error);
    }
  }

  /**
   * ユーザーの詳細情報を取得（関連データも含む）
   * @param {string} uid - ユーザーUID
   */
  async getUserDetails(uid) {
    try {
      const [userData, studentData, mentorData] = await Promise.all([
        this.get(uid),
        this.studentsService.get(uid),
        this.mentorsService.get(uid)
      ]);

      return {
        user: userData,
        student: studentData,
        mentor: mentorData,
        role: userData?.role || USER_ROLES.STUDENT
      };
    } catch (error) {
      console.error('Error getting user details:', error);
      throw this.handleError(error);
    }
  }
}

const userService = new UserService();
export default userService;
